import { ReactNode, useState, useEffect } from 'react'
import { Wallet } from 'lucide-react'
import AssetIcon from './AssetIcon'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useWalletStore } from '@/store/walletStore'

interface AssetCardProps {
  icon?: ReactNode
  logoUrl?: string
  name: string
  value: number
  change: string
  code: string
  isPositive?: boolean
  borderColor?: string
  usdValue?: string
  layout?: 'grid' | 'row'
  networkName?: string
  networkLogoUrl?: string
  /** ID de la red del token (ej. 'bsc', 'omdb') — al hacer click, la red activa cambia a esta */
  network?: string
}

const NetworkBadge = ({
  networkName,
  networkLogoUrl
}: {
  networkName?: string
  networkLogoUrl?: string
}) => {
  if (!networkName) return null

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-card-hover border border-border-base w-max">
      <div className="w-4 h-4 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center shrink-0">
        {networkLogoUrl ? (
          <img
            src={networkLogoUrl}
            alt={networkName}
            className="w-full h-full object-cover"
          />
        ) : (
          <Wallet className="w-2.5 h-2.5 text-primary" />
        )}
      </div>
      <span className="text-[11px] font-medium text-text-secondary">
        {networkName}
      </span>
    </div>
  )
}

const AssetCard = ({
  icon,
  logoUrl,
  name,
  code,
  value,
  usdValue,
  change,
  isPositive = true,
  borderColor = 'border-primary',
  layout = 'grid',
  networkName,
  networkLogoUrl,
  network
}: AssetCardProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setSelectedNetwork = useWalletStore((state) => state.setSelectedNetwork)
  const [imgState, setImgState] = useState<'primary' | 'fallback' | 'error'>(
    'primary'
  )

  const handleOpenDetails = () => {
    if (network) setSelectedNetwork(network)
    navigate('/asset-details/' + code)
  }

  // Sin logo propio, usa el de la red pero en gris para no confundirlo con el token real
  const hasOwnLogo = !!logoUrl
  const effectiveLogoUrl = logoUrl || networkLogoUrl

  useEffect(() => {
    setImgState('primary')
  }, [effectiveLogoUrl])

  // Si la URL primaria falla, intenta la misma dirección en la ruta de ethereum de Trust Wallet
  const getEthereumFallback = (url: string): string | null => {
    const match = url.match(/blockchains\/[^/]+\/assets\/([^/]+)\/logo\.png/)
    if (!match) return null
    const fallback = `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/${match[1]}/logo.png`
    return fallback !== url ? fallback : null
  }

  const handleImgError = () => {
    if (imgState === 'primary' && effectiveLogoUrl) {
      const fallback = getEthereumFallback(effectiveLogoUrl)
      if (fallback) {
        setImgState('fallback')
        return
      }
    }
    setImgState('error')
  }

  const activeSrc =
    imgState === 'fallback' && effectiveLogoUrl
      ? getEthereumFallback(effectiveLogoUrl)
      : effectiveLogoUrl

  const iconContent =
    activeSrc && imgState !== 'error' ? (
      <img
        key={activeSrc}
        src={activeSrc}
        alt={name}
        onError={handleImgError}
        className={`w-full h-full rounded-full object-cover p-0.5 ${hasOwnLogo ? '' : 'grayscale'}`}
      />
    ) : (
      icon
    )

  // Números muy largos (>7 dígitos) usan una fuente más chica y se truncan con "..."
  const formattedValue = value.toLocaleString(undefined, {
    maximumFractionDigits: 6
  })
  const isLongValue = formattedValue.replace(/[^0-9]/g, '').length > 7

  return (
    <>
      {/* MOBILE LAYOUT */}
      <div
        onClick={handleOpenDetails}
        className="flex flex-row shadow-md lg:hidden border-t border-border-base bg-card rounded-3xl justify-between gap-4 p-6 items-center transition-opacity duration-200 ease-in-out hover:opacity-80 cursor-pointer"
      >
        <div className="flex flex-row gap-5 items-center min-w-0">
          <AssetIcon borderColor={borderColor}>{iconContent}</AssetIcon>
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className="font-bold text-lg text-text-main truncate">
              {name}
            </h3>
            <p className="text-text-secondary text-sm truncate">{code}</p>
            <NetworkBadge
              networkName={networkName}
              networkLogoUrl={networkLogoUrl}
            />
          </div>
        </div>
        <div className="flex flex-col gap-0.5 items-end min-w-0 shrink-0 max-w-[45%]">
          <h3
            className={`font-bold text-text-main truncate w-full text-right ${isLongValue ? 'text-sm' : 'text-lg'}`}
            title={formattedValue}
          >
            {formattedValue}
          </h3>
          <p
            className={`text-xs ${isPositive ? 'text-success' : 'text-accent'}`}
          >
            {change}
          </p>
        </div>
      </div>

      {/* DESKTOP LAYOUT - Grid (Assets page) */}
      {layout === 'grid' && (
        <div
          onClick={handleOpenDetails}
          className="hidden lg:flex shadow-md border-t border-border-base bg-card rounded-3xl p-6 transition-opacity duration-200 ease-in-out hover:opacity-80 cursor-pointer"
        >
          <div className="flex flex-col gap-6 w-full">
            <div className="flex justify-between items-start">
              <AssetIcon size="w-12 h-12" borderColor={borderColor}>
                {iconContent}
              </AssetIcon>

              <div className="flex flex-col text-end">
                <p
                  className={`text-sm font-semibold ${isPositive ? 'text-success' : 'text-accent'}`}
                >
                  {change}
                </p>
                <p className="text-xs mb-2 text-text-secondary uppercase">
                  {t('assets.change_24h')}
                </p>
                <NetworkBadge
                  networkName={networkName}
                  networkLogoUrl={networkLogoUrl}
                />
              </div>
            </div>

            <div className="flex flex-col gap-0.5 mt-auto min-w-0">
              <h3 className="font-semibold text-xs text-text-secondary uppercase truncate">
                {name}
              </h3>
              <h2
                className={`font-bold text-text-main truncate ${isLongValue ? 'text-lg' : 'text-2xl'}`}
                title={`${formattedValue} ${code}`}
              >
                {formattedValue} {code}
              </h2>
              <h3 className="font-medium text-xs text-text-secondary truncate">
                {usdValue}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* DESKTOP LAYOUT - Row (Dashboard) */}
      {layout === 'row' && (
        <div
          onClick={handleOpenDetails}
          className="hidden lg:flex flex-col shadow-md border-t border-border-base bg-card rounded-3xl p-6 w-1/3 transition-opacity duration-200 ease-in-out hover:opacity-80 cursor-pointer"
        >
          <div className="flex flex-col gap-3 items-start">
            <AssetIcon borderColor={borderColor}>{iconContent}</AssetIcon>
            <h3 className="font-bold text-lg text-text-main">{name}</h3>
          </div>
          <div className="flex flex-col gap-0.5 items-start mt-auto min-w-0 w-full">
            <h3
              className={`font-bold text-text-main truncate w-full ${isLongValue ? 'text-sm' : 'text-lg'}`}
              title={`${formattedValue} ${code}`}
            >
              {formattedValue} {code}
            </h3>
            <p
              className={`text-xs ${isPositive ? 'text-success' : 'text-accent'}`}
            >
              {change}
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default AssetCard
